import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { addNotification } from '../../store/slices/uiSlice';
import { useDispatch } from 'react-redux';

import { API_URL } from '../../config/api';

const SetNewPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('requestId');
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!requestId) {
      dispatch(addNotification({
        type: 'error',
        message: 'Invalid password reset link'
      }));
      navigate('/login');
    }
  }, [requestId, navigate, dispatch]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      dispatch(addNotification({
        type: 'error',
        message: 'Passwords do not match'
      }));
      return;
    }

    if (formData.newPassword.length < 6) {
      dispatch(addNotification({
        type: 'error',
        message: 'Password must be at least 6 characters long'
      }));
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_URL}/password-reset/set-password`, {
        requestId: requestId,
        newPassword: formData.newPassword
      });
      
      setSuccess(true);
      dispatch(addNotification({
        type: 'success',
        message: 'Password has been reset successfully!'
      }));
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error('Failed to set new password:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || error.response?.data || 'Failed to set new password'
      }));
    } finally {
      setLoading(false);
    }
  };

  if (!requestId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="rounded-lg bg-red-50 dark:bg-red-900 dark:bg-opacity-20 border border-red-200 dark:border-red-800 p-6">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">
                  Invalid Request
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 mb-4">
                  Please provide a valid password reset request ID in the URL (e.g., /set-password?requestId=...)
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm font-medium text-red-800 dark:text-red-200 hover:underline"
                >
                  Back to Login
                </button>
                <p className="mt-3 text-sm flex flex-wrap items-center gap-x-2 gap-y-1">
                  <Link
                    to="/privacy"
                    className="font-medium text-red-800 dark:text-red-200 hover:underline"
                  >
                    Privacy Policy
                  </Link>
                  <span className="text-red-600 dark:text-red-400" aria-hidden>
                    ·
                  </span>
                  <Link
                    to="/terms"
                    className="font-medium text-red-800 dark:text-red-200 hover:underline"
                  >
                    Terms
                  </Link>
                  <span className="text-red-600 dark:text-red-400" aria-hidden>
                    ·
                  </span>
                  <Link
                    to="/support"
                    className="font-medium text-red-800 dark:text-red-200 hover:underline"
                  >
                    Support
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="rounded-lg bg-green-50 dark:bg-green-900 dark:bg-opacity-20 border border-green-200 dark:border-green-800 p-6">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-lg font-medium text-green-800 dark:text-green-200 mb-2">
                  Password Reset Successful!
                </h3>
                <p className="text-sm text-green-700 dark:text-green-300">
                  Your password has been reset successfully. Redirecting to login...
                </p>
                <p className="mt-3 text-sm flex flex-wrap items-center gap-x-2 gap-y-1">
                  <Link
                    to="/privacy"
                    className="font-medium text-green-800 dark:text-green-200 hover:underline"
                  >
                    Privacy Policy
                  </Link>
                  <span className="text-green-600 dark:text-green-400" aria-hidden>
                    ·
                  </span>
                  <Link
                    to="/terms"
                    className="font-medium text-green-800 dark:text-green-200 hover:underline"
                  >
                    Terms
                  </Link>
                  <span className="text-green-600 dark:text-green-400" aria-hidden>
                    ·
                  </span>
                  <Link
                    to="/support"
                    className="font-medium text-green-800 dark:text-green-200 hover:underline"
                  >
                    Support
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Set your password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Choose a password so you can log in to Workloop.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                New Password
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                required
                minLength={6}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Enter new password (min 6 characters)"
                value={formData.newPassword}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                minLength={6}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Setting Password...' : 'Set New Password'}
            </button>
          </div>

          <div className="text-center text-sm flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <Link
              to="/privacy"
              className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400"
            >
              Privacy Policy
            </Link>
            <span className="text-gray-400 dark:text-gray-500" aria-hidden>
              ·
            </span>
            <Link
              to="/terms"
              className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400"
            >
              Terms
            </Link>
            <span className="text-gray-400 dark:text-gray-500" aria-hidden>
              ·
            </span>
            <Link
              to="/support"
              className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400"
            >
              Support
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetNewPassword;

